import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Monitor, LaptopMinimal, Download, Zap, History, Cloud, Lock,
  CheckCircle2, Sparkles, ArrowDown, GraduationCap,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { apiGet } from '../api';
import { trackEvent } from '../analytics/plausible';
import { useInstallerDownload, detectOS, type OS, type Variant } from '../hooks/useInstallerDownload';
import SmartScreenHelp from '../components/SmartScreenHelp';
import { deviceKind, runsDesktopApp, type DeviceKind } from '../utils/device';
import '../App.css';

interface VersionInfo {
  version: string;
  platform: string;
  channel: string;
  releaseDate: string;
  releaseNotes: string;
  mandatory: boolean;
}

const WIN_VARIANTS = [
  { arch: 'Installer', sub: 'Recommended for most writers', size: '88 MB', key: 'installer' },
  { arch: 'Portable', sub: 'No installation: runs from a folder', size: '91 MB', key: 'portable' },
] as const;

// Mac ships a single .pkg installer (no portable build). Size is approximate.
const MAC_INSTALLER = { size: '100 MB', req: 'Apple Silicon & Intel' } as const;

// isSubscriber comes from App; unset means a plan, as for tests (the server is the gate).
const DownloadsPage: React.FC<{ isSubscriber?: boolean }> = ({ isSubscriber = true }) => {
  const navigate = useNavigate();
  const [os] = useState<OS>(detectOS);
  const [device] = useState<DeviceKind>(deviceKind);
  // A phone or tablet is told to use the bookshelf here; a desktop we ship no build for
  // is told so by name. Neither is handed an installer. (#724, #733)
  const isMobile = device === 'phone' || device === 'tablet';
  const noBuildFor = device === 'chromebook' ? 'ChromeOS' : device === 'linux' ? 'Linux' : null;
  // Everything that claims an installer matches this visitor.
  const hasInstaller = runsDesktopApp(device);
  const [linkState, setLinkState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [macVersion, setMacVersion] = useState<VersionInfo | null>(null);
  const [toast, setToast] = useState(false);
  const { downloading, downloadError, smartScreen, closeSmartScreen, start } = useInstallerDownload('downloads');
  const platformsRef = useRef<HTMLElement>(null);

  const isMac = os === 'mac';

  // href, not a rebuilt path: a visitor who arrived on ?utm_source=... would
  // otherwise copy a link with the attribution stripped off.
  const shareUrl = () => window.location.href;

  // Share sheet first — it is better supported inside in-app browsers than the
  // Clipboard API, and "send this to myself" is the actual intent. Clipboard is the
  // fallback, and a failure is shown rather than swallowed: a button that visibly
  // does nothing just gets tapped again.
  const shareOrCopyLink = async () => {
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'Chaptery for desktop', url: shareUrl() });
        trackEvent('Handoff Link Shared');
        return;
      }
      await navigator.clipboard.writeText(shareUrl());
      trackEvent('Handoff Link Shared');
      setLinkState('copied');
      window.setTimeout(() => setLinkState('idle'), 2500);
    } catch (e) {
      // Dismissing the share sheet rejects with AbortError; that is not a failure.
      if ((e as Error)?.name === 'AbortError') return;
      setLinkState('failed');
    }
  };

  useEffect(() => {
    apiGet(`/updates/check?platform=win`)
      .then(r => r.body.json() as unknown as Promise<VersionInfo>)
      .then(data => setVersionInfo(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiGet('/updates/check?platform=mac')
      .then(r => r.body.json() as unknown as Promise<VersionInfo>)
      .then(data => setMacVersion(data))
      .catch(() => {});
  }, []);

  // Without a plan every download is refused (#1059): say so up front, not after the
  // click, and drop the free-trial small print. Wording approved by the owner.
  const choosePlan = (
    <div className="dl-choose-plan">
      <p>Choose a plan to download Chaptery.</p>
      <Link to="/pricing" className="btn-primary dl-btn-block">See plans</Link>
    </div>
  );

  // On a phone or a computer with no build, the books are what the card offers: without a
  // plan they are refused, so the card says what the home page says (#1059).
  const booksSafe = (
    <div className="dl-choose-plan">
      <p>Your books and world are safe.</p>
      <p>Choose a plan to open them again.</p>
      <Link to="/pricing" className="btn-primary dl-btn-block">See plans</Link>
    </div>
  );

  // On Windows the SmartScreen dialog says the download has started, and the toast
  // would sit on top of it (it is above the overlay) for its 3.6 seconds.
  const handleDownload = async (variant: Variant = 'installer', platform: OS = 'windows', trigger: HTMLElement | null = null) => {
    const ok = await start(variant, platform, trigger);
    if (ok && platform !== 'windows') {
      setToast(true);
      window.setTimeout(() => setToast(false), 3600);
    }
  };

  const scrollToPlatforms = () => {
    if (platformsRef.current) {
      window.scrollTo({ top: platformsRef.current.offsetTop - 20, behavior: 'smooth' });
    }
  };

  return (
    <>
    <Helmet>
      <title>Download Chaptery | Desktop Novel Writing App for Mac &amp; Windows</title>
      <meta name="description" content="Download Chaptery for Mac and Windows. Free 30-day trial, no credit card required. Draft your manuscript, build your world, and sync to the cloud." />
      <link rel="canonical" href="https://trychaptery.com/downloads" />
    </Helmet>
    <div className="downloads-page">

      {/* ---- Hero ---- */}
      <section className="dl-hero">
        <div className="dl-hero-inner">
          <span className="dl-eyebrow"><Download size={13} /> Desktop App</span>
          <h1>Download Chaptery</h1>
          <p className="dl-hero-sub">
            {isMobile || noBuildFor
              ? 'The desktop app runs on Mac and Windows. Everything you have written is already here, and the browser tools work on this device.'
              : 'The full writing and world-building studio: a fast, focused app with your books and notes backed up safely to the cloud.'}
          </p>

          {noBuildFor ? (
            /* A laptop with no build of ours. Unlike a phone, the browser tools are
               full-size here, so this leads with that rather than apologising. */
            <div className="dl-primary-card dl-primary-mobile">
              <div className="dl-primary-detected">
                <Sparkles size={13} /> {noBuildFor}
              </div>
              <div className="dl-primary-os">
                <Cloud size={34} />
              </div>
              <h2>No {noBuildFor} build yet</h2>
              <p className="dl-soon-copy">
                Chaptery&rsquo;s desktop app is built for Mac and Windows. Everything you
                write is saved to your account, so you can work in this browser in the
                meantime, and pick up the desktop app on a Mac or Windows machine
                whenever you have one.
              </p>
              {isSubscriber ? (
                <>
                  <button
                    className="btn-primary dl-btn-block"
                    onClick={() => navigate('/books')}
                  >
                    <Sparkles size={18} /> Open my books
                  </button>
                  <p className="dl-primary-fineprint">
                    Free 30-day trial &middot; No credit card required
                  </p>
                </>
              ) : booksSafe}
            </div>
          ) : isMobile ? (
            /* Neither installer runs on a phone. Offering them as the primary action
               is a dead end — and the trial genuinely does work in this browser, so
               that is the honest thing to put first. */
            <div className="dl-primary-card dl-primary-mobile">
              <div className="dl-primary-detected">
                <Sparkles size={13} /> On this device
              </div>
              <div className="dl-primary-os">
                <Cloud size={34} />
              </div>
              <h2>Your bookshelf is right here</h2>
              {/* Deliberately narrow. The world builder is a graph canvas with no
                  responsive styling and the storyboard picker overflows a 390px
                  viewport, so naming them here would send someone from a dead end to
                  a worse one. The bookshelf genuinely does collapse to one column. */}
              <p className="dl-soon-copy">
                The desktop app runs on Mac and Windows. Everything you have written is
                already saved to your account. Open your books here, or send yourself
                the link to install on a computer later.
              </p>
              {isSubscriber ? (
                <button
                  className="btn-primary dl-btn-block"
                  onClick={() => navigate('/books')}
                >
                  <Sparkles size={18} /> Open my books
                </button>
              ) : booksSafe}
              <div className="dl-mobile-actions">
                <button className="btn-secondary btn-sm" onClick={shareOrCopyLink}>
                  {linkState === 'copied' ? 'Link copied'
                    : linkState === 'failed' ? 'Copy failed' : 'Send myself the link'}
                </button>
              </div>
              {linkState === 'failed' && (
                <p className="dl-link-fallback">
                  Copying was blocked here. The address is{' '}
                  <strong>{shareUrl()}</strong>
                </p>
              )}
              {isSubscriber && (
                <p className="dl-primary-fineprint">
                  Free 30-day trial · No credit card required
                </p>
              )}
            </div>
          ) : isMac ? (
            <div className="dl-primary-card">
              <div className="dl-primary-detected">
                <Sparkles size={13} /> Recommended for you
              </div>
              <div className="dl-primary-os">
                <LaptopMinimal size={34} />
              </div>
              <h2>Chaptery for Mac</h2>
              <p className="dl-primary-meta">
                {macVersion ? `v${macVersion.version} · ` : ''}{MAC_INSTALLER.size} · {MAC_INSTALLER.req}
              </p>
              {isSubscriber ? (
                <>
                  {downloadError && <p className="dl-download-error">{downloadError}</p>}
                  <button
                    className="btn-primary dl-btn-block"
                    onClick={() => handleDownload('installer', 'mac')}
                    disabled={downloading}
                  >
                    <Download size={18} />
                    {downloading ? 'Preparing download…' : 'Download for Mac'}
                  </button>
                  <p className="dl-primary-fineprint">
                    Free 30-day trial · No credit card required
                  </p>
                </>
              ) : choosePlan}
            </div>
          ) : (
            <div className="dl-primary-card">
              <div className="dl-primary-detected">
                <Sparkles size={13} /> Recommended for you
              </div>
              <div className="dl-primary-os">
                <Monitor size={34} />
              </div>
              <h2>Chaptery for Windows</h2>
              <p className="dl-primary-meta">
                {versionInfo ? `v${versionInfo.version} · ` : ''}{WIN_VARIANTS[0].size} · Windows 10 &amp; 11
              </p>
              {isSubscriber ? (
                <>
                  {downloadError && <p className="dl-download-error">{downloadError}</p>}
                  <button
                    className="btn-primary dl-btn-block"
                    onClick={e => handleDownload('installer', 'windows', e.currentTarget)}
                    disabled={downloading}
                  >
                    <Download size={18} />
                    {downloading ? 'Preparing download…' : 'Download for Windows'}
                  </button>
                  <p className="dl-primary-fineprint">
                    Free 30-day trial · No credit card required
                  </p>
                </>
              ) : choosePlan}
            </div>
          )}

          <button className="dl-hero-otherlink" onClick={scrollToPlatforms}>
            <ArrowDown size={15} />
            {isMobile || noBuildFor ? 'Desktop installers (Mac & Windows)' : 'See all platforms'}
          </button>
        </div>
      </section>

      {/* ---- Trust strip ---- */}
      <section className="dl-trust">
        <div className="dl-trust-item">
          <span className="dl-trust-icon teal"><Zap size={26} /></span>
          <h3>Fast and native</h3>
          <p>A real desktop application, not a browser tab. Fast, focused, and built for long writing sessions.</p>
        </div>
        <div className="dl-trust-item">
          <span className="dl-trust-icon green"><History size={26} /></span>
          <h3>Autosave &amp; history</h3>
          <p>Chaptery saves as you write and keeps your earlier drafts, so nothing is ever lost.</p>
        </div>
        <div className="dl-trust-item">
          <span className="dl-trust-icon orange"><Cloud size={26} /></span>
          <h3>Cloud sync</h3>
          <p>Your books and world notes back up automatically and follow you across devices.</p>
        </div>
        <div className="dl-trust-item">
          <span className="dl-trust-icon teal"><Lock size={26} /></span>
          <h3>Yours, always</h3>
          <p>Your manuscript stays private and belongs to you. No one else can read your work.</p>
        </div>
      </section>

      {/* ---- All platforms ---- */}
      <section className="dl-platforms" ref={platformsRef}>
        <div className="dl-section-head">
          <h2>Choose your platform</h2>
          <p>Pick the version that matches your computer.</p>
          {!isSubscriber && choosePlan}
        </div>

        <div className="dl-platform-grid">
          <div className={`dl-platform-card${os === 'mac' && hasInstaller ? ' is-detected' : ''}`}>
            <div className="dl-platform-head">
              <span className="dl-platform-icon"><LaptopMinimal size={26} /></span>
              <div className="dl-platform-titles">
                <h3>Mac</h3>
                <p className="dl-platform-req">Apple Silicon &amp; Intel</p>
              </div>
              {os === 'mac' && hasInstaller && <span className="dl-detected-pill">Your system</span>}
            </div>
            <div className="dl-variants">
              <div className="dl-variant">
                <div className="dl-variant-info">
                  <span className="dl-variant-arch">Installer</span>
                  <span className="dl-variant-meta">
                    {macVersion ? `v${macVersion.version} · ` : ''}{MAC_INSTALLER.size} · macOS 12+
                  </span>
                </div>
                {isSubscriber && (
                  <button
                    className="btn-secondary btn-sm dl-variant-btn"
                    onClick={() => handleDownload('installer', 'mac')}
                    disabled={downloading}
                  >
                    <Download size={15} /> Download
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className={`dl-platform-card${os === 'windows' && hasInstaller ? ' is-detected' : ''}`}>
            <div className="dl-platform-head">
              <span className="dl-platform-icon"><Monitor size={26} /></span>
              <div className="dl-platform-titles">
                <h3>Windows</h3>
                <p className="dl-platform-req">Windows 10 &amp; 11</p>
              </div>
              {os === 'windows' && hasInstaller && <span className="dl-detected-pill">Your system</span>}
            </div>
            <div className="dl-variants">
              {WIN_VARIANTS.map(v => (
                <div className="dl-variant" key={v.key}>
                  <div className="dl-variant-info">
                    <span className="dl-variant-arch">{v.arch}</span>
                    <span className="dl-variant-meta">{v.sub} · {v.size}</span>
                  </div>
                  {isSubscriber && (
                    <button
                      className="btn-secondary btn-sm dl-variant-btn"
                      onClick={e => handleDownload(v.key, 'windows', e.currentTarget)}
                      disabled={downloading}
                    >
                      <Download size={15} /> Download
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---- Get started ---- */}
      <section className="dl-steps-section">
        <div className="dl-section-head">
          <h2>Up and running in minutes</h2>
          <p>From download to your first chapter: three quick steps.</p>
        </div>
        <div className="dl-steps">
          <div className="dl-step">
            <span className="dl-step-n">1</span>
            <h3>Download &amp; install</h3>
            <p>Open the file you downloaded and follow the prompts. Chaptery opens ready to go.</p>
          </div>
          <div className="dl-step">
            <span className="dl-step-n">2</span>
            <h3>Sign in</h3>
            <p>Sign in so your books and world notes back up to your Bookshelf and sync across devices.</p>
          </div>
          <div className="dl-step">
            <span className="dl-step-n">3</span>
            <h3>Start your book</h3>
            <p>Create a new book or bring in a Word document, and start organizing your world.</p>
          </div>
        </div>
        <div className="dl-steps-cta">
          <button className="btn-primary" onClick={() => navigate('/help/tutorial')}>
            <GraduationCap size={17} /> Follow the getting-started tutorial
          </button>
        </div>
      </section>

      {/* ---- Toast ---- */}
      <div className={`dl-toast${toast ? ' show' : ''}`} role="status" aria-live="polite">
        <CheckCircle2 size={18} />
        <span>Starting download…</span>
      </div>

      {smartScreen && (
        <SmartScreenHelp
          app={smartScreen.app}
          portable={smartScreen.portable}
          returnFocusTo={smartScreen.trigger}
          onClose={closeSmartScreen}
        />
      )}

    </div>
    </>
  );
};

export default DownloadsPage;
